import {authorizeQuery, authorizeRes, logoutQuery, getAssociatesQuery} from '../queries/auth';
import Cookies from 'js-cookie';
import {useRecoilState, useSetRecoilState} from 'recoil';
import {useEffect, useState} from 'react';
import {session as sessionState, userState} from '../states/session';
import {LOGIN_EXTERNAL_LINK} from '../constants/env';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {Portals, searchKeys} from '../types/helpers';
import {urlWithParams} from '../helpers/Route';
import {UserT} from '../queries/types/user';
import {filterAssociatesByDepartment} from '../helpers/roles';
import {getBrandFromCookie, setBrandCookie, brands} from './brand';
import {route} from '../constants/routes';

type TokenType = string | null | undefined;

export const getToken = (): TokenType => {
  return Cookies.get('id_token');
};

export const setToken = (token: TokenType, expires?: number): TokenType | void => {
  if (!token) {
    return Cookies.remove('id_token');
  }
  return Cookies.set('id_token', token, {expires: expires || 365});
};

export const getTokenJingle = (): TokenType => {
  return Cookies.get('id_token_j');
};

export const setTokenJingle = (token: TokenType, expires?: number): TokenType | void => {
  if (!token) {
    return Cookies.remove('id_token_j');
  }
  return Cookies.set('id_token_j', token, {expires: expires || 365});
};

export function useFetchSession() {
  const [searchParams, setSearchParams] = useSearchParams();
  const paramToken = searchParams.get(searchKeys.token);
  const paramTokenJ = searchParams.get(searchKeys.token_j);
  const paramExpires = searchParams.get(searchKeys.expires);
  const brandCookie = getBrandFromCookie();

  const [loading, setLoading] = useState(true);
  const [success, SetSuccess] = useState(false);
  const checkAuth = useCheckAuthorize();
  const setSession = useSetRecoilState(sessionState);
  const [mounted, setMounted] = useState(false);
  const token = getToken();
  const tokenJ = getTokenJingle();

  useEffect(() => {
    if (mounted) return;
    setMounted(true);
    const setAuthorize = async () => {
      if (!token && !tokenJ && !paramToken && !paramTokenJ) return;

      //check token from params
      if (paramToken || paramTokenJ) {
        if (paramToken) setBrandCookie(brands.enc);
        if (paramTokenJ) setBrandCookie(brands.jingle);
        if (paramToken) setToken(paramToken, Number(paramExpires));
        if (paramTokenJ) setTokenJingle(paramTokenJ, Number(paramExpires));
        const res2 = await checkAuth();
        if (res2) {
          setSession({sessionToken: paramToken || paramTokenJ || '', user: res2});
          SetSuccess(true);
        }
        searchParams.delete(searchKeys.token);
        searchParams.delete(searchKeys.token_j);
        searchParams.delete(searchKeys.expires);
        setSearchParams(searchParams);
        setLoading(false);
        return;
      }

      //check exist token
      if (token || tokenJ) {
        if (token && !brandCookie) setBrandCookie(brands.enc);
        if (tokenJ && !brandCookie) setBrandCookie(brands.jingle);
        const res = await checkAuth();
        if (res) {
          setSession({sessionToken: token || tokenJ || '', user: res});
          SetSuccess(true);
          setLoading(false);
          searchParams.delete(searchKeys.token);
          searchParams.delete(searchKeys.expires);
          searchParams.delete(searchKeys.token_j);
          setSearchParams(searchParams);
          return;
        }
      }

      setLoading(false);
    };
    setAuthorize();
  }, []);

  if (!mounted) return {loading: true, success};
  if (!token && !tokenJ && !paramToken && !paramTokenJ) {
    setToken(null);
    setTokenJingle(null);
    setSession(null);
    return {loading: false, success};
  }
  return {loading, success};
}

export const useCheckAuthorize = () => {
  const nav = useNavigate();
  const check = async () => {
    try {
      const res = await authorizeQuery();
      return res?.body as authorizeRes;
    } catch (e) {
      nav(route.redirectToLogin.path);
      return null;
    }
  };
  return check;
};

export const useViewer = () => {
  const user = useRecoilState(userState);

  return user[0];
};

export const useLogOut = () => async () => {
  const res = await logoutQuery();
  if (res?.ok) {
    setToken(null);
    setTokenJingle(null);
    LOGIN_EXTERNAL_LINK && window.location.replace(urlWithParams(LOGIN_EXTERNAL_LINK, true, Portals.groups));
  }
};

export const useGetAssociates = () => {
  const [associates, setAssociates] = useState<UserT[]>([]);
  const viewer = useViewer();

  const fetch = async () => {
    try {
      const res = await getAssociatesQuery();
      setAssociates(res?.body);
    } catch (e) {
      return null;
    }
  };
  useEffect(() => {
    fetch();
  }, []);
  return {
    associates,
    associatesOptions: filterAssociatesByDepartment(['Group Ticket'], viewer?.departments, associates)?.map((el) => ({
      departments: el.departments,
      label: `${el?.firstName} ${el?.email}`,
      value: el?.email,
    })),
  };
};
